import { useMutation, useQuery } from '@tanstack/react-query';
import type { UserCodeEntry, UserDirectoryEntry, UserMapAssignment } from '@testflow/shared';
import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Combobox,
  type ComboboxHandle,
  ComboboxList,
  ComboboxNotice,
  ComboboxOption,
} from '../../components/combobox.js';
import { importApi } from '../../lib/import-api.js';
import { matchesQuery } from '../../lib/row-filter.js';
import { translateImportError } from './import-errors.js';

// Task 10 (#65): Wizard-Schritt zwischen Mapping und Vorschau — ordnet Sachbearbeiter-Kürzel
// ("MWALK") echten Nutzern zu, legt Platzhalter an oder überspringt sie. Jede Zeile kommt vom
// Backend bereits mit einem geratenen Namen (guessNameFromCode) und bis zu N Kandidaten
// (matchUsers) vorgeschlagen — das bleibt aber immer ein Vorschlag: die Aktion ist erst gewählt,
// wenn ein Mensch sie bestätigt (HITL, wie im Mapping-Schritt).
//
// Doppelzellen-Hinweis: GET /user-codes liefert DISTINCT-Kürzel über alle Blätter/Zeilen hinweg
// (routes/import.ts) — welche zwei Kürzel aus derselben "/"-Zelle stammten, wird dabei nicht
// mitgeführt. Eine Pro-Kürzel-Markierung ("dieses Kürzel teilte sich eine Zelle mit jenem") ist
// aus den vorhandenen Daten also nicht rekonstruierbar; das würde eine Backend-Änderung brauchen
// (out of scope hier). Deshalb nur ein statischer Hinweistext, der die Regel erklärt, statt
// erfundener Pro-Kürzel-Daten.

type Action = 'assign' | 'stub' | 'ignore';

export interface RowState {
  action: Action;
  userId: string;
  name: string;
  email: string;
}

function initialRow(entry: UserCodeEntry): RowState {
  return {
    action: entry.mapped_user_id ? 'assign' : 'stub',
    userId: entry.mapped_user_id ?? '',
    name: entry.guessed_name,
    email: '',
  };
}

function buildAssignment(code: string, row: RowState): UserMapAssignment {
  if (row.action === 'assign') return { code, action: 'assign', user_id: row.userId };
  if (row.action === 'stub') {
    const email = row.email.trim();
    return { code, action: 'stub', name: row.name.trim(), email: email || undefined };
  }
  return { code, action: 'ignore' };
}

// #73 (Review I1): Der Zustand des Schritts lebt im Wizard (ImportPage), nicht in der Komponente.
// „Zurück“ aus der Vorschau mountet den Schritt neu; ein zuvor ignoriertes Kürzel hat keine
// gespeicherte Zuordnung und stünde sonst wieder auf „Platzhalter“ — „Weiter“ legte dann einen
// Platzhalter-Nutzer an und verwürfe den fertigen Entwurf (minutenlange Neuübersetzung).
// `saved` hält je Kürzel, was zuletzt erfolgreich gespeichert wurde: gesendet wird nur, was davon
// abweicht — ein schon angelegter Platzhalter entsteht so nicht ein zweites Mal.
export interface UserStepState {
  rows: Record<string, RowState>;
  saved: Record<string, UserMapAssignment>;
}

export const EMPTY_USER_STEP_STATE: UserStepState = { rows: {}, saved: {} };

function sameAssignment(a: UserMapAssignment, b: UserMapAssignment | undefined): boolean {
  return (
    b !== undefined &&
    a.action === b.action &&
    a.user_id === b.user_id &&
    a.name === b.name &&
    a.email === b.email
  );
}

// Ein Vorschlag mit fehlender Auswahl darf nie stillschweigend als "ignore" durchgehen (HITL) —
// lieber "Weiter" blockieren und den Menschen auf die fehlende Entscheidung hinweisen.
function isRowValid(row: RowState): boolean {
  if (row.action === 'assign') return row.userId.trim().length > 0;
  if (row.action === 'stub') return row.name.trim().length > 0;
  return true;
}

// #78/#82-Muster: eine "vernuenftige Zahl" fuer die Kandidaten-Trefferliste — begrenzt UND
// angeschrieben (stille Kappung liest sich wie Vollstaendigkeit). Gleicher Wert wie
// AddMemberPicker (catalog/e2e-tab.tsx), aus demselben Grund.
const USER_SEARCH_LIMIT = 30;

type UserCandidate = UserDirectoryEntry;

// Vereinheitlichte Anzeige-Form fuer die Dropdown-Liste — deckt sowohl einen fuzzy Vorschlag
// (mit score) als auch einen per Freitext gefundenen Kandidaten (ohne score) ab, damit `shown`
// unten EIN Array-Typ bleibt statt eines Unions aus zwei Array-Typen (TS kann `.map` sonst nicht
// sauber typisieren).
type DisplayCandidate = { user_id: string; name: string; email: string; score?: number };

function userLabel(u: { name: string; email: string }): string {
  return `${u.name} — ${u.email}`;
}

// #75 Teil 1: Ersetzt den <select>, dessen Tastatursuche wie beim 204er (#82) nur am Wortanfang
// gematcht hätte. Vorlage ist AddMemberPicker (catalog/e2e-tab.tsx) — vollständig übernommen,
// inklusive der beiden teuer erarbeiteten a11y-Details (tabIndex={-1} auf dem Options-Button,
// onMouseDown mit preventDefault — beide leben in components/combobox.tsx), der
// Pfeiltasten-Navigation und des Kappungs-Hinweises. Anders als dort ist die Auswahl EINSTUFIG
// (kein "+ Hinzufügen"): ein Klick/Enter auf eine Option setzt die Zuordnung sofort, wie es der
// abgelöste <select> per onChange auch tat.
function UserPicker({
  code,
  users,
  suggestions,
  value,
  onChange,
}: {
  code: string;
  users: UserCandidate[];
  suggestions: UserCodeEntry['matches']; // fuzzy Top-5, inkl. bereits zugeordnetem Nutzer, mit Score
  value: string; // gewählte user_id, '' wenn keine
  onChange: (userId: string) => void;
}) {
  const { t } = useTranslation();
  const comboRef = useRef<ComboboxHandle>(null);
  const initial =
    users.find((u) => u.user_id === value) ?? suggestions.find((s) => s.user_id === value);
  const initialLabel = initial ? userLabel(initial) : '';
  const [query, setQuery] = useState(initialLabel);
  // Siehe M3-Kommentar in AddMemberPicker (e2e-tab.tsx): haelt fest, welcher Text zur aktuellen
  // Auswahl gehoert, damit ein erneutes Fokussieren ohne weiteres Tippen nicht faelschlich "Kein
  // Treffer" zeigt.
  const [selectedLabel, setSelectedLabel] = useState<string | null>(initialLabel || null);
  const [highlighted, setHighlighted] = useState(-1);
  const listboxId = useId();

  const trimmedQuery = query.trim();
  const searching = trimmedQuery.length > 0 && query !== selectedLabel;
  const filtered: DisplayCandidate[] = users.filter((u) =>
    matchesQuery(trimmedQuery, u.name, u.email),
  );
  const suggestionItems: DisplayCandidate[] = suggestions.map((s) => ({
    user_id: s.user_id,
    name: s.name,
    email: s.email,
    score: s.score,
  }));
  // Leeres Suchfeld: die fuzzy Vorschläge zuerst (mit Score) — nicht die volle Liste. Erst ein
  // Suchbegriff schaltet auf die volle, gekappte Trefferliste um.
  const shown = searching ? filtered.slice(0, USER_SEARCH_LIMIT) : suggestionItems;
  const activeIndex = highlighted >= 0 ? Math.min(highlighted, shown.length - 1) : 0;

  const choose = (u: DisplayCandidate): void => {
    const label = userLabel(u);
    setQuery(label);
    setSelectedLabel(label);
    setHighlighted(-1);
    onChange(u.user_id);
  };

  const onInputKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (shown.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted(Math.min(activeIndex + 1, shown.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(Math.max(activeIndex - 1, 0));
    } else if (e.key === 'Enter') {
      const target = shown[activeIndex];
      if (target) {
        e.preventDefault();
        choose(target);
        comboRef.current?.close();
      }
    }
  };

  return (
    <div>
      <Combobox
        ref={comboRef}
        label={t('import.users.userFor', { code })}
        placeholder={t('import.users.userSearchPlaceholder')}
        value={query}
        onChange={(v) => {
          setQuery(v);
          setHighlighted(-1);
          // Branch-Review B1: Beim Weglassen des Zweitschritts ("+ Hinzufügen") muss die Invariante
          // aus AddMemberPicker (e2e-tab.tsx:133-138) mitkommen: getippter Text hebt die bestehende
          // Auswahl auf. Sonst zeigt das Feld "Bru", `row.userId` steht aber weiter auf dem zuvor
          // gewählten bzw. dem aus `mapped_user_id` vorbelegten Nutzer — `isRowValid` ist wahr und
          // "Weiter" schreibt eine Zuordnung, die NIEMAND bestätigt hat (HITL-Bruch). Mit dem Leeren
          // blockiert `isRowValid` stattdessen das Weiter.
          if (v !== selectedLabel) {
            setSelectedLabel(null);
            onChange('');
          }
        }}
        onKeyDown={onInputKeyDown}
        listboxId={listboxId}
        {...(shown.length > 0 ? { activeId: `${listboxId}-option-${activeIndex}` } : {})}
        className="rounded-btn border border-[var(--border)] bg-bg-subtle px-2 py-1 text-xs text-text-body"
      >
        {({ open, close }) =>
          open && (shown.length > 0 || searching) ? (
            <ComboboxList id={listboxId}>
              {shown.map((u, i) => (
                <ComboboxOption
                  key={u.user_id}
                  id={`${listboxId}-option-${i}`}
                  selected={i === activeIndex}
                  onSelect={() => {
                    choose(u);
                    close();
                  }}
                  className={`block w-full px-2 py-1 text-left text-text-body hover:bg-brand-accent/10 ${
                    i === activeIndex ? 'bg-brand-accent/10' : ''
                  }`}
                >
                  {typeof u.score === 'number'
                    ? t('import.users.matchOption', {
                        name: u.name,
                        email: u.email,
                        score: Math.round(u.score * 100),
                      })
                    : userLabel(u)}
                </ComboboxOption>
              ))}
              {searching && filtered.length === 0 && (
                <ComboboxNotice>
                  {t('catalog.search.noResults', { query: trimmedQuery })}
                </ComboboxNotice>
              )}
              {searching && filtered.length > shown.length && (
                <ComboboxNotice>
                  {t('catalog.search.resultsLimited', {
                    shown: shown.length,
                    total: filtered.length,
                  })}
                </ComboboxNotice>
              )}
            </ComboboxList>
          ) : null
        }
      </Combobox>
    </div>
  );
}

function UserRow({
  entry,
  users,
  row,
  onChange,
}: {
  entry: UserCodeEntry;
  users: UserCandidate[];
  row: RowState;
  onChange: (patch: Partial<RowState>) => void;
}) {
  const { t } = useTranslation();

  return (
    <tr>
      <td className="px-4 py-2 font-mono text-text-body">{entry.code}</td>
      <td className="px-4 py-2 text-text-body">{entry.guessed_name}</td>
      <td className="px-4 py-2">
        <label htmlFor={`action-${entry.code}`} className="sr-only">
          {t('import.users.actionFor', { code: entry.code })}
        </label>
        <select
          id={`action-${entry.code}`}
          aria-label={t('import.users.actionFor', { code: entry.code })}
          value={row.action}
          onChange={(e) => {
            const action = e.target.value as Action;
            // #75: eine bereits getroffene Nutzerauswahl darf beim Umschalten auf stub/ignore
            // nicht stillschweigend erhalten bleiben — sonst könnte ein späteres Zurückschalten
            // auf "assign" einen veralteten Stand ohne erneute menschliche Bestätigung reaktivieren.
            onChange({ action, userId: action === 'assign' ? row.userId : '' });
          }}
          className="rounded-btn border border-[var(--border)] bg-bg-subtle px-2 py-1 text-xs text-text-body"
        >
          <option value="assign">{t('import.users.actions.assign')}</option>
          <option value="stub">{t('import.users.actions.stub')}</option>
          <option value="ignore">{t('import.users.actions.ignore')}</option>
        </select>
      </td>
      <td className="px-4 py-2">
        {row.action === 'assign' && (
          <>
            <UserPicker
              code={entry.code}
              users={users}
              suggestions={entry.matches}
              value={row.userId}
              onChange={(userId) => onChange({ userId })}
            />
            {entry.matches.length === 0 && (
              <p className="mt-1 text-[11px] text-text-muted">{t('import.users.noMatches')}</p>
            )}
          </>
        )}
        {row.action === 'stub' && (
          <div className="flex flex-col gap-1">
            <label htmlFor={`name-${entry.code}`} className="sr-only">
              {t('import.users.nameFor', { code: entry.code })}
            </label>
            <input
              id={`name-${entry.code}`}
              aria-label={t('import.users.nameFor', { code: entry.code })}
              value={row.name}
              onChange={(e) => onChange({ name: e.target.value })}
              className="rounded-btn border border-[var(--border)] bg-bg-subtle px-2 py-1 text-xs text-text-body"
            />
            <label htmlFor={`email-${entry.code}`} className="sr-only">
              {t('import.users.emailFor', { code: entry.code })}
            </label>
            <input
              id={`email-${entry.code}`}
              type="email"
              placeholder={t('import.users.email')}
              aria-label={t('import.users.emailFor', { code: entry.code })}
              value={row.email}
              onChange={(e) => onChange({ email: e.target.value })}
              className="rounded-btn border border-[var(--border)] bg-bg-subtle px-2 py-1 text-xs text-text-body"
            />
          </div>
        )}
        {row.action === 'ignore' && <span className="text-[11px] text-text-muted">—</span>}
      </td>
    </tr>
  );
}

export function UsersStep({
  projectId,
  jobId,
  onConfirmed,
  state,
  onStateChange,
}: {
  projectId: string;
  jobId: string;
  onConfirmed: () => void;
  state: UserStepState;
  onStateChange: (update: (prev: UserStepState) => UserStepState) => void;
}) {
  const { t } = useTranslation();
  const { rows } = state;
  const [validationError, setValidationError] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['import-user-codes', projectId, jobId],
    queryFn: () => importApi.getUserCodes(projectId, jobId),
  });

  const codes = data?.codes ?? [];
  const users = data?.users ?? [];

  // Zeilen einmalig aus den geladenen Kürzeln vorbelegen — danach lebt der Zustand nur noch in
  // `rows` (kein Re-Init bei einem Query-Refetch oder nach „Zurück“, sonst gingen manuelle
  // Änderungen verloren).
  useEffect(() => {
    if (!data) return;
    onStateChange((prev) =>
      Object.keys(prev.rows).length > 0
        ? prev
        : { ...prev, rows: Object.fromEntries(data.codes.map((c) => [c.code, initialRow(c)])) },
    );
  }, [data, onStateChange]);

  const saveMutation = useMutation({
    mutationFn: (assignments: UserMapAssignment[]) =>
      importApi.saveUserMap(projectId, jobId, assignments),
    meta: { successToast: 'import.users.saved' },
    onSuccess: (_res, assignments) => {
      onStateChange((prev) => ({
        ...prev,
        saved: { ...prev.saved, ...Object.fromEntries(assignments.map((a) => [a.code, a])) },
      }));
      onConfirmed();
    },
  });

  function updateRow(entry: UserCodeEntry, patch: Partial<RowState>): void {
    onStateChange((prev) => ({
      ...prev,
      rows: {
        ...prev.rows,
        [entry.code]: { ...(prev.rows[entry.code] ?? initialRow(entry)), ...patch },
      },
    }));
  }

  function handleContinue(): void {
    setValidationError(false);
    const invalid = codes.some((c) => !isRowValid(rows[c.code] ?? initialRow(c)));
    if (invalid) {
      setValidationError(true);
      return;
    }
    const changed = codes
      .map((c) => buildAssignment(c.code, rows[c.code] ?? initialRow(c)))
      .filter((a) => !sameAssignment(a, state.saved[a.code]));
    // #73 (Review I1): nichts geändert seit dem letzten Speichern → kein Request, der Entwurf bleibt.
    if (changed.length === 0) {
      onConfirmed();
      return;
    }
    saveMutation.mutate(changed);
  }

  if (isLoading) return <div className="text-text-muted">{t('common.loading')}</div>;
  if (error) {
    return (
      <p role="alert" className="text-xs text-status-fail">
        {translateImportError(t, error instanceof Error ? error.message : 'user_codes_failed')}
      </p>
    );
  }

  if (codes.length === 0) {
    return (
      <div>
        <p className="text-text-muted">{t('import.users.empty')}</p>
        <button
          type="button"
          onClick={onConfirmed}
          className="mt-4 rounded-btn bg-brand-accent px-4 py-2 text-sm font-medium text-white"
        >
          {t('import.users.continue')}
        </button>
      </div>
    );
  }

  return (
    <div>
      <p className="mb-4 rounded-card border border-dashed border-[var(--border)] bg-[var(--tint-blue)] px-4 py-3 text-xs text-text-body">
        {t('import.users.doubleCellNote')}
      </p>
      <div className="mb-6 rounded-card border border-[var(--border)] bg-bg-surface">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-text-muted">
              <th className="px-4 py-2">{t('import.users.code')}</th>
              <th className="px-4 py-2">{t('import.users.guessedName')}</th>
              <th className="px-4 py-2">{t('import.users.action')}</th>
              <th className="px-4 py-2">{t('import.users.assignment')}</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((c) => (
              <UserRow
                key={c.code}
                entry={c}
                users={users}
                row={rows[c.code] ?? initialRow(c)}
                onChange={(patch) => updateRow(c, patch)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {validationError && (
        <p role="alert" className="mb-3 text-xs text-status-fail">
          {t('import.users.validationError')}
        </p>
      )}
      {saveMutation.isError && (
        <p role="alert" className="mb-3 text-xs text-status-fail">
          {translateImportError(
            t,
            saveMutation.error instanceof Error ? saveMutation.error.message : 'user_map_failed',
          )}
        </p>
      )}

      <button
        type="button"
        disabled={saveMutation.isPending}
        onClick={handleContinue}
        className="rounded-btn bg-brand-accent px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
      >
        {t('import.users.continue')}
      </button>
    </div>
  );
}
